import { KeyboardEventHandler, Ref, useEffect } from 'react';
import {
  Autocomplete,
  AutocompleteInputChangeReason,
  Box,
  Button,
  TextField,
} from '@mui/material';

interface RemoteAutocompleteProps<Option> {
  label: string;
  options: Option[];
  value: Option | null;
  inputValue: string;
  loading?: boolean;
  isError?: boolean;
  disabled?: boolean;
  shouldDebounceInput?: boolean;
  error?: boolean;
  helperText?: string;
  minSearchLength?: number;
  debounceMs?: number;
  thresholdText?: string;
  noOptionsText?: string;
  errorText?: string;
  createActionLabel?: string;
  canCreate?: boolean;
  autoHighlight?: boolean;
  autoFocus?: boolean;
  inputRef?: Ref<HTMLInputElement>;
  onKeyDown?: KeyboardEventHandler<HTMLDivElement>;
  // eslint-disable-next-line no-unused-vars
  getOptionLabel: (option: Option) => string;
  // eslint-disable-next-line no-unused-vars
  isOptionEqualToValue: (option: Option, value: Option) => boolean;
  // eslint-disable-next-line no-unused-vars
  onInputChange: (
    // eslint-disable-next-line no-unused-vars
    value: string,
    // eslint-disable-next-line no-unused-vars
    reason: AutocompleteInputChangeReason
  ) => void;
  // eslint-disable-next-line no-unused-vars
  onDebouncedInputChange: (value: string) => void;
  // eslint-disable-next-line no-unused-vars
  onChange: (value: Option | null) => void;
  onCreateOption?: () => void;
}

export function RemoteAutocomplete<Option>({
  label,
  options,
  value,
  inputValue,
  loading = false,
  isError = false,
  disabled = false,
  shouldDebounceInput = true,
  error = false,
  helperText,
  minSearchLength = 3,
  debounceMs = 400,
  thresholdText = `Digite pelo menos ${minSearchLength} caracteres para buscar`,
  noOptionsText = 'Nenhum registro encontrado',
  errorText = 'Não foi possível buscar os registros',
  createActionLabel,
  canCreate = false,
  autoHighlight = true,
  autoFocus = false,
  inputRef,
  onKeyDown,
  getOptionLabel,
  isOptionEqualToValue,
  onInputChange,
  onDebouncedInputChange,
  onChange,
  onCreateOption,
}: RemoteAutocompleteProps<Option>) {
  const trimmedInputValue = inputValue.trim();
  const normalizedInputValue = trimmedInputValue.toLocaleUpperCase();
  const normalizedSelectedLabel = value
    ? getOptionLabel(value).trim().toLocaleUpperCase()
    : '';
  const hasSelectedValueMatchingInput =
    !!value && normalizedSelectedLabel === normalizedInputValue;
  const isBelowThreshold = trimmedInputValue.length < minSearchLength;
  const optionsWithSelectedValue =
    value && !options.some((option) => isOptionEqualToValue(option, value))
      ? [value, ...options]
      : options;
  const shouldShowCreateAction =
    !loading &&
    !isError &&
    !hasSelectedValueMatchingInput &&
    !isBelowThreshold &&
    options.length === 0 &&
    canCreate &&
    !!createActionLabel &&
    !!onCreateOption;

  useEffect(() => {
    if (!shouldDebounceInput) return undefined;

    if (trimmedInputValue.length < minSearchLength) {
      onDebouncedInputChange('');
      return undefined;
    }

    const timeoutId = window.setTimeout(() => {
      onDebouncedInputChange(trimmedInputValue);
    }, debounceMs);

    return () => window.clearTimeout(timeoutId);
  }, [
    debounceMs,
    minSearchLength,
    onDebouncedInputChange,
    shouldDebounceInput,
    trimmedInputValue,
  ]);

  const getNoOptionsText = () => {
    if (isError) return errorText;
    if (isBelowThreshold) return thresholdText;

    if (!shouldShowCreateAction) return noOptionsText;

    return (
      <Box>
        <Box sx={{ mb: 1 }}>{noOptionsText}</Box>
        <Button
          size="small"
          onMouseDown={(event) => event.preventDefault()}
          onClick={(event) => {
            event.stopPropagation();
            onCreateOption();
          }}
        >
          {createActionLabel}
        </Button>
      </Box>
    );
  };

  return (
    <Autocomplete
      options={optionsWithSelectedValue}
      value={value}
      inputValue={inputValue}
      loading={loading}
      disabled={disabled}
      autoHighlight={autoHighlight}
      onKeyDown={onKeyDown}
      getOptionLabel={getOptionLabel}
      isOptionEqualToValue={isOptionEqualToValue}
      filterOptions={(currentOptions) =>
        currentOptions.filter((option) => {
          if (!value || !isOptionEqualToValue(option, value)) return true;

          return getOptionLabel(option)
            .toLocaleUpperCase()
            .includes(normalizedInputValue);
        })
      }
      noOptionsText={getNoOptionsText()}
      loadingText="Buscando..."
      onInputChange={(_, newInputValue, reason) =>
        onInputChange(newInputValue, reason)
      }
      onChange={(_, newValue) => onChange(newValue)}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          autoFocus={autoFocus}
          inputRef={inputRef}
          error={error}
          helperText={helperText}
        />
      )}
    />
  );
}
